/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  PropsWithChildren,
  ReactNode
} from 'react';
import {Drawer, makeStyles} from '@material-ui/core';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {useIsPhone} from '../../hooks/useMedia';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import {dlsColor, dlsTypography, dlsZIndex} from '../../theme/theme';
import {IconButton} from '../buttons/IconButton';
import GridActions from '../grid/GridActions';

export interface GridDrawerProps {
  actionMenu?: ReactNode;
  title?: string;
  onClose?: MouseEventHandler<HTMLButtonElement>;
  onGridRefresh?: () => void;
  onGridExport?: (params?: DownloadTemplate) => void;
  isExporting?: boolean;
  open?: boolean;
  isPaginated?: boolean;
}

type StyleProps = {
  isPaginated?: boolean;
  isPhone?: boolean;
}

const PAGINATION_HEIGHT = 53;
const VERTICAL_PADDING_HEIGHT = 25;
const HEADER_HEIGHT = 108;

const useStyles = makeStyles(() => ({
  drawerRoot : {
    zIndex : `${dlsZIndex('dashboardDrawerRoot')} !important` as any
  },
  drawerPhoneRoot : {
    zindex : dlsZIndex('dashboardDrawerRoot') as any
  },
  drawerContent : {
    width           : '50vw',
    height          : '100%',
    padding         : `${VERTICAL_PADDING_HEIGHT}px 35px`,
    backgroundColor : dlsColor('grey8')
  },
  drawerHeader : {
  },
  drawerControls : {
  },
  closeIcon : {
    marginLeft : '-15px'
  },
  moreIcon : {
  },
  titleHeader : {
    display        : 'flex',
    justifyContent : 'space-between'
  },
  title : {
    ...dlsTypography('h1'),
    paddingLeft : '16px',
    fontSize    : 26,
    lineHeight  : ({isPhone}: StyleProps) => dlsTypography('h1', 'lineHeight', isPhone ? 'mobile' : 'desktop')
  },
  grid : {
    height : ({isPaginated}: StyleProps) => `calc(100vh - ${calculateGridHeightOffset(isPaginated)}px)`
  }
}));

const calculateGridHeightOffset = (isPaginated = false): number => {
  return (isPaginated ? PAGINATION_HEIGHT : 0) + HEADER_HEIGHT + (VERTICAL_PADDING_HEIGHT * 2);
};

/**
 * Container for displaying a drawer with a grid, includes a small grid action menu.
 */
export const GridDrawer = ({
  children: grid,
  actionMenu,
  title,
  onClose,
  onGridRefresh,
  onGridExport,
  isPaginated = false,
  isExporting = false,
  open = false
}: PropsWithChildren<GridDrawerProps>) => {
  const isPhone = useIsPhone();
  const classes = useStyles({isPhone, isPaginated});
  return (
    <Drawer open={open}
      anchor={'right'}
      onClose={onClose}
      classes={{
        root : !isPhone ? classes.drawerRoot : classes.drawerPhoneRoot
      }}>
      <div className={classes.drawerContent}>
        <div className={classes.drawerHeader}>
          <div className={classes.drawerControls}>
            <div className={classes.closeIcon}>
              <IconButton icon={<MaterialExitCloseIcon color={dlsColor('grey3')} />}
                onClick={onClose} />
            </div>
            <div className={classes.moreIcon}>
              {actionMenu}
            </div>
          </div>
          <div className={classes.titleHeader}>
            <span className={classes.title}>
              {title}
            </span>
            <GridActions onRefresh={onGridRefresh}
              onExport={onGridExport}
              isExporting={isExporting} />
          </div>
        </div>
        <div className={classes.grid}>
          {grid}
        </div>
      </div>
    </Drawer>
  );
};

export default GridDrawer;
